import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Bank, ArrowUpRight, ArrowDownLeft, ArrowsLeftRight, Trash, PencilSimple,
  Wallet, CreditCard, Money, Eye, EyeSlash, Receipt, MinusCircle, CaretUp, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import ExpenseFormDialog from "@/components/ExpenseFormDialog";
import TreasuryGrowthChart from "@/components/banks/TreasuryGrowthChart";

const KINDS = [
  { v: "cash",   label: "Efectivo",  Icon: Money,      color: "#00873D" },
  { v: "debit",  label: "Débito",    Icon: Wallet,     color: "#002FA7" },
  { v: "credit", label: "Crédito",   Icon: CreditCard, color: "#7E57C2" },
  { v: "other",  label: "Otro",      Icon: Bank,       color: "#52525B" },
];

const fmt = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TX_LABELS = {
  deposit: { label: "Depósito", color: "#00873D", Icon: ArrowDownLeft },
  withdraw: { label: "Retiro", color: "#E0000F", Icon: ArrowUpRight },
  sale_payment: { label: "Pago de venta", color: "#00873D", Icon: Receipt },
  payment_reversal: { label: "Reversión pago", color: "#E0000F", Icon: ArrowUpRight },
  transfer_in: { label: "Transferencia ↘", color: "#00873D", Icon: ArrowDownLeft },
  transfer_out: { label: "Transferencia ↗", color: "#E0000F", Icon: ArrowUpRight },
};

const EMPTY = { name: "", currency: "MXN", kind: "cash", notes: "", visible_in_sales: true };

export default function Banks() {
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [adjBank, setAdjBank] = useState(null);
  const [adjType, setAdjType] = useState("deposit");
  const [adjForm, setAdjForm] = useState({ amount: "", note: "" });
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferForm, setTransferForm] = useState({ from_bank_id: "", to_bank_id: "", amount: "", exchange_rate: "", note: "" });
  const [txBank, setTxBank] = useState(null);
  const [txs, setTxs] = useState([]);
  const [editingTx, setEditingTx] = useState(null);  // tx being edited
  const [txDraft, setTxDraft] = useState({ amount: "", note: "" });
  const [deletingTx, setDeletingTx] = useState(null);  // tx pending delete
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expenseBank, setExpenseBank] = useState(null); // bank for which to register an expense
  const [fxRate, setFxRate] = useState(17);

  const load = async () => {
    const [bs, st] = await Promise.all([
      api.get("/banks"),
      api.get("/settings").catch(() => ({ data: { exchange_rate: 17 } })),
    ]);
    setBanks(bs.data);
    setFxRate(Number(st.data?.exchange_rate || 17));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, currency: b.currency, kind: b.kind, notes: b.notes || "", visible_in_sales: b.visible_in_sales !== false }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/banks/${editing.id}`, form);
      else await api.post("/banks", form);
      toast.success(editing ? "Banco actualizado" : "Banco creado");
      setShowForm(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const openAdj = (b, type) => { setAdjBank(b); setAdjType(type); setAdjForm({ amount: "", note: "" }); };
  const submitAdj = async (e) => {
    e.preventDefault();
    const amt = parseFloat(adjForm.amount);
    if (!amt || amt <= 0) return toast.error("Monto inválido");
    try {
      await api.post(`/banks/${adjBank.id}/${adjType}`, { amount: amt, note: adjForm.note });
      toast.success(adjType === "deposit" ? "Depósito registrado" : "Retiro registrado");
      setAdjBank(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    const src = banks.find((b)=>b.id===transferForm.from_bank_id);
    const dst = banks.find((b)=>b.id===transferForm.to_bank_id);
    if (!src || !dst) return toast.error("Selecciona ambos bancos");
    try {
      await api.post("/banks/transfer", {
        from_bank_id: transferForm.from_bank_id,
        to_bank_id: transferForm.to_bank_id,
        amount: parseFloat(transferForm.amount),
        exchange_rate: transferForm.exchange_rate ? parseFloat(transferForm.exchange_rate) : null,
        note: transferForm.note,
      });
      toast.success("Transferencia realizada");
      setShowTransfer(false);
      setTransferForm({ from_bank_id: "", to_bank_id: "", amount: "", exchange_rate: "", note: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const openTxs = async (b) => {
    setTxBank(b);
    try { setTxs((await api.get(`/banks/${b.id}/transactions`)).data); }
    catch { toast.error("Error"); }
  };

  const reloadTxs = async () => {
    if (!txBank) return;
    try {
      const [bs, tt] = await Promise.all([
        api.get("/banks"),
        api.get(`/banks/${txBank.id}/transactions`),
      ]);
      setBanks(bs.data);
      const updated = bs.data.find((b) => b.id === txBank.id);
      if (updated) setTxBank(updated);
      setTxs(tt.data);
    } catch { /* ignore */ }
  };

  const startEditTx = (t) => {
    setEditingTx(t);
    setTxDraft({ amount: Math.abs(t.amount), note: t.note || "" });
  };

  const submitEditTx = async (e) => {
    e.preventDefault();
    if (!editingTx) return;
    const amt = parseFloat(txDraft.amount);
    if (!amt || amt <= 0) return toast.error("Monto inválido");
    try {
      await api.patch(`/banks/${editingTx.bank_id}/transactions/${editingTx.id}`, {
        amount: amt, note: txDraft.note,
      });
      toast.success("Movimiento actualizado · saldo recalculado");
      setEditingTx(null);
      reloadTxs();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const confirmDeleteTx = async () => {
    if (!deletingTx) return;
    try {
      await api.delete(`/banks/${deletingTx.bank_id}/transactions/${deletingTx.id}`);
      toast.success("Movimiento eliminado · saldo revertido");
      setDeletingTx(null);
      reloadTxs();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try { await api.delete(`/banks/${toDelete.id}`); toast.success("Banco eliminado"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setToDelete(null); }
  };

  // Totals breakdown by kind+currency
  const sumBy = (currency, kind) => banks
    .filter((b) => b.currency === currency && b.kind === kind)
    .reduce((s, b) => s + (b.balance || 0), 0);
  const mxnCash   = sumBy("MXN", "cash");
  const mxnDebit  = sumBy("MXN", "debit");
  const mxnCredit = sumBy("MXN", "credit");
  const mxnOther  = sumBy("MXN", "other");
  const usdCash   = sumBy("USD", "cash");
  const usdDebit  = sumBy("USD", "debit");
  const usdCredit = sumBy("USD", "credit");
  const usdOther  = sumBy("USD", "other");
  const totalMXN  = mxnCash + mxnDebit + mxnCredit + mxnOther;
  const totalUSD  = usdCash + usdDebit + usdCredit + usdOther;
  // Convert all to MXN and USD using settings.exchange_rate
  const totalAllMxn = totalMXN + totalUSD * (fxRate || 17);
  const totalAllUsd = totalUSD + (totalMXN / (fxRate || 17));

  // Banks are already ordered by the backend (sort_order → name). We only add
  // helpers to let the super_admin move a card up/down manually.
  const sortedBanks = banks;

  const persistOrder = async (newList) => {
    setBanks(newList); // optimistic UI
    try {
      await api.post("/banks/reorder", { order: newList.map((b) => b.id) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al reordenar");
      load();
    }
  };
  const moveBank = (id, direction) => {
    const idx = banks.findIndex((b) => b.id === id);
    if (idx < 0) return;
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= banks.length) return;
    const next = [...banks];
    [next[idx], next[target]] = [next[target], next[idx]];
    persistOrder(next);
  };
  const toggleVisibility = async (b) => {
    const nextVisible = !(b.visible_in_sales !== false);
    try {
      await api.put(`/banks/${b.id}`, {
        name: b.name, currency: b.currency, kind: b.kind, notes: b.notes || "",
        visible_in_sales: nextVisible,
      });
      toast.success(nextVisible ? "Visible para vendedores" : "Oculto para vendedores");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const needsXR = transferForm.from_bank_id && transferForm.to_bank_id &&
    banks.find(b=>b.id===transferForm.from_bank_id)?.currency !==
    banks.find(b=>b.id===transferForm.to_bank_id)?.currency;

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">TESORERÍA</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Bancos</h1>
          <p className="text-zinc-500 mt-1 text-sm">{banks.length} bolsas registradas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={()=>setShowTransfer(true)} data-testid="transfer-btn" disabled={banks.length<2} className="gap-2">
            <ArrowsLeftRight size={16} /> Transferir
          </Button>
          <Button onClick={openNew} className="bg-[#002FA7] hover:bg-[#002277] text-white gap-2" data-testid="new-bank-btn">
            <Plus size={16} weight="bold" /> Nuevo banco
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* MXN breakdown */}
        <div className="border border-zinc-200 p-5 bg-white" data-testid="kpi-mxn-breakdown">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">MXN</div>
            <span className="text-[10px] font-mono-data px-1.5 py-0.5 bg-zinc-100 rounded">PESOS</span>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1.5 text-zinc-600"><Money size={14} weight="duotone" color="#00873D"/> Efectivo</span><span className="numeric font-semibold">MX${Number(mxnCash).toLocaleString("en-US",{minimumFractionDigits:2})}</span></div>
            <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1.5 text-zinc-600"><Wallet size={14} weight="duotone" color="#002FA7"/> Débito</span><span className="numeric font-semibold">MX${Number(mxnDebit).toLocaleString("en-US",{minimumFractionDigits:2})}</span></div>
            <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1.5 text-zinc-600"><CreditCard size={14} weight="duotone" color="#7E57C2"/> Crédito</span><span className="numeric font-semibold">MX${Number(mxnCredit).toLocaleString("en-US",{minimumFractionDigits:2})}</span></div>
            {mxnOther !== 0 && <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1.5 text-zinc-600"><Bank size={14} weight="duotone" color="#52525B"/> Otro</span><span className="numeric font-semibold">MX${Number(mxnOther).toLocaleString("en-US",{minimumFractionDigits:2})}</span></div>}
          </div>
          <div className="border-t border-zinc-200 mt-3 pt-2 flex items-center justify-between">
            <span className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Total MXN</span>
            <span className="numeric text-xl font-bold text-[#00873D]" data-testid="kpi-mxn-total">MX${Number(totalMXN).toLocaleString("en-US",{minimumFractionDigits:2})}</span>
          </div>
        </div>

        {/* USD breakdown */}
        <div className="border border-zinc-200 p-5 bg-white" data-testid="kpi-usd-breakdown">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">USD</div>
            <span className="text-[10px] font-mono-data px-1.5 py-0.5 bg-zinc-100 rounded">DÓLARES</span>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1.5 text-zinc-600"><Money size={14} weight="duotone" color="#00873D"/> Efectivo</span><span className="numeric font-semibold">${Number(usdCash).toLocaleString("en-US",{minimumFractionDigits:2})}</span></div>
            <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1.5 text-zinc-600"><Wallet size={14} weight="duotone" color="#002FA7"/> Débito</span><span className="numeric font-semibold">${Number(usdDebit).toLocaleString("en-US",{minimumFractionDigits:2})}</span></div>
            <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1.5 text-zinc-600"><CreditCard size={14} weight="duotone" color="#7E57C2"/> Crédito</span><span className="numeric font-semibold">${Number(usdCredit).toLocaleString("en-US",{minimumFractionDigits:2})}</span></div>
            {usdOther !== 0 && <div className="flex items-center justify-between"><span className="inline-flex items-center gap-1.5 text-zinc-600"><Bank size={14} weight="duotone" color="#52525B"/> Otro</span><span className="numeric font-semibold">${Number(usdOther).toLocaleString("en-US",{minimumFractionDigits:2})}</span></div>}
          </div>
          <div className="border-t border-zinc-200 mt-3 pt-2 flex items-center justify-between">
            <span className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Total USD</span>
            <span className="numeric text-xl font-bold text-[#00873D]" data-testid="kpi-usd-total">${Number(totalUSD).toLocaleString("en-US",{minimumFractionDigits:2})}</span>
          </div>
        </div>

        {/* Patrimonio consolidado */}
        <div className="border-2 border-[#002FA7] p-5 bg-white" data-testid="kpi-consolidated">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Patrimonio consolidado</div>
            <span className="text-[10px] font-mono-data px-1.5 py-0.5 bg-[#002FA7] text-white rounded">TC {Number(fxRate).toFixed(2)}</span>
          </div>
          <div className="space-y-2">
            <div>
              <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Todo en pesos</div>
              <div className="numeric text-3xl font-extrabold text-[#002FA7] mt-0.5" data-testid="kpi-total-all-mxn">
                MX${Number(totalAllMxn).toLocaleString("en-US",{minimumFractionDigits:2})}
              </div>
            </div>
            <div className="pt-2 border-t border-zinc-100">
              <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Todo en dólares</div>
              <div className="numeric text-3xl font-extrabold text-[#00873D] mt-0.5" data-testid="kpi-total-all-usd">
                ${Number(totalAllUsd).toLocaleString("en-US",{minimumFractionDigits:2})}
              </div>
            </div>
          </div>
        </div>
      </section>

      <TreasuryGrowthChart refreshKey={banks} />

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="banks-grid">
        {sortedBanks.length === 0 && (
          <div className="col-span-full dotted-bg py-16 text-center text-zinc-400">Sin bancos. Crea el primero.</div>
        )}
        {sortedBanks.map((b, idx) => {
          const kind = KINDS.find((k)=>k.v===b.kind) || KINDS[3];
          const Icon = kind.Icon;
          const isVisible = b.visible_in_sales !== false;
          const isFirst = idx === 0;
          const isLast = idx === sortedBanks.length - 1;
          return (
            <div key={b.id} className={`border bg-white p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all ${isVisible ? "border-zinc-200" : "border-dashed border-zinc-300 bg-zinc-50/40"}`} data-testid={`bank-card-${b.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-sm" style={{ background: kind.color + "20" }}>
                    <Icon size={20} weight="duotone" color={kind.color} />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-lg leading-tight">{b.name}</div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: kind.color+"20", color: kind.color }}>{kind.label}</span>
                      <span className="text-[10px] font-mono-data px-1.5 py-0.5 bg-zinc-100 rounded">{b.currency}</span>
                      <button
                        type="button"
                        onClick={() => toggleVisibility(b)}
                        className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded transition-colors ${isVisible ? "bg-[#00873D]/10 text-[#00873D] hover:bg-[#00873D]/20" : "bg-zinc-200 text-zinc-600 hover:bg-zinc-300"}`}
                        title={isVisible ? "Visible para vendedores en notas de venta · clic para ocultar" : "Interno · oculto para vendedores · clic para mostrar"}
                        data-testid={`toggle-visibility-${b.id}`}
                      >
                        {isVisible ? <Eye size={10} weight="bold"/> : <EyeSlash size={10} weight="bold"/>}
                        {isVisible ? "Visible" : "Interno"}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex gap-0.5">
                    <button onClick={()=>moveBank(b.id, "up")} disabled={isFirst} className="p-1 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded disabled:opacity-30 disabled:cursor-not-allowed" title="Subir" data-testid={`bank-up-${b.id}`}>
                      <CaretUp size={12} weight="bold"/>
                    </button>
                    <button onClick={()=>moveBank(b.id, "down")} disabled={isLast} className="p-1 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded disabled:opacity-30 disabled:cursor-not-allowed" title="Bajar" data-testid={`bank-down-${b.id}`}>
                      <CaretDown size={12} weight="bold"/>
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={()=>openEdit(b)} className="p-1.5 text-zinc-400 hover:text-[#002FA7] hover:bg-zinc-100 rounded" data-testid={`edit-bank-${b.id}`}>
                      <PencilSimple size={14}/>
                    </button>
                    <button onClick={()=>setToDelete(b)} className="p-1.5 text-zinc-400 hover:text-[#E0000F] hover:bg-red-50 rounded">
                      <Trash size={14}/>
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-5 pb-4 border-b border-zinc-100">
                <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Saldo</div>
                <div className={`numeric text-3xl font-bold ${b.balance < 0 ? "text-[#E0000F]" : "text-zinc-900"}`} data-testid={`balance-${b.id}`}>
                  {fmt(b.balance, b.currency)}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-1">
                <Button size="sm" variant="outline" onClick={()=>openAdj(b,"deposit")} className="gap-1 text-xs text-[#00873D] border-[#00873D]/30 hover:bg-[#00873D]/5" data-testid={`dep-${b.id}`}>
                  <ArrowDownLeft size={12} /> Ingreso
                </Button>
                <Button size="sm" variant="outline" onClick={()=>openAdj(b,"withdraw")} className="gap-1 text-xs text-[#E0000F] border-[#E0000F]/30 hover:bg-[#E0000F]/5" data-testid={`wit-${b.id}`}>
                  <ArrowUpRight size={12} /> Retiro
                </Button>
                <Button size="sm" variant="outline" onClick={()=>setExpenseBank(b)} className="gap-1 text-xs text-[#7E57C2] border-[#7E57C2]/30 hover:bg-[#7E57C2]/5" data-testid={`exp-${b.id}`}>
                  <MinusCircle size={12} /> Gasto
                </Button>
                <Button size="sm" variant="outline" onClick={()=>openTxs(b)} className="gap-1 text-xs" data-testid={`txs-${b.id}`}>
                  <Eye size={12} /> Movs.
                </Button>
              </div>
            </div>
          );
        })}
      </section>

      {/* Bank form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md" data-testid="bank-form-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Editar banco" : "Nuevo banco"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} required placeholder="Inbursa, Nu, Caja chica..." data-testid="bank-name" className="mt-1"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Moneda</Label>
                <Select value={form.currency} onValueChange={(v)=>setForm({...form,currency:v})}>
                  <SelectTrigger className="mt-1" data-testid="bank-currency"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">MXN</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Tipo</Label>
                <Select value={form.kind} onValueChange={(v)=>setForm({...form,kind:v})}>
                  <SelectTrigger className="mt-1" data-testid="bank-kind"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {KINDS.map((k)=><SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Notas</Label>
              <Textarea value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} rows={2} className="mt-1"/>
            </div>
            <div className="flex items-start justify-between gap-3 border border-zinc-200 rounded-sm p-3 bg-zinc-50/50">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-800">Visible en notas de venta</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Cuando está activado, los vendedores pueden seleccionar este banco al registrar pagos.
                  Desactívalo para bancos <b>administrativos internos</b>.
                </div>
              </div>
              <Switch
                checked={form.visible_in_sales !== false}
                onCheckedChange={(v)=>setForm({...form, visible_in_sales: v})}
                data-testid="bank-visible-toggle"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="bank-submit">{saving ? "Guardando…" : (editing ? "Actualizar" : "Crear")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjustment (deposit/withdraw) */}
      <Dialog open={!!adjBank} onOpenChange={(o)=>{ if (!o) setAdjBank(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {adjType === "deposit" ? "Agregar dinero" : "Retirar dinero"}
            </DialogTitle>
            <div className="text-xs text-zinc-500">{adjBank?.name} · {adjBank?.currency} · Saldo actual: <span className="numeric font-semibold">{fmt(adjBank?.balance, adjBank?.currency)}</span></div>
          </DialogHeader>
          <form onSubmit={submitAdj} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Monto ({adjBank?.currency})</Label>
              <Input type="number" step="0.01" min="0.01" value={adjForm.amount} onChange={(e)=>setAdjForm({...adjForm,amount:e.target.value})} required autoFocus className="mt-1 numeric" data-testid="adj-amount"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nota / motivo</Label>
              <Textarea value={adjForm.note} onChange={(e)=>setAdjForm({...adjForm,note:e.target.value})} rows={2} className="mt-1" placeholder="Saldo inicial, pago en efectivo, etc."/>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setAdjBank(null)}>Cancelar</Button>
              <Button type="submit" className={adjType==="deposit"?"bg-[#00873D] hover:bg-[#006e30] text-white":"bg-[#E0000F] hover:bg-[#b8000c] text-white"} data-testid="adj-submit">
                {adjType === "deposit" ? "Agregar" : "Retirar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer */}
      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">Transferir entre bancos</DialogTitle></DialogHeader>
          <form onSubmit={submitTransfer} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Desde</Label>
              <Select value={transferForm.from_bank_id} onValueChange={(v)=>setTransferForm({...transferForm,from_bank_id:v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Banco origen..."/></SelectTrigger>
                <SelectContent>
                  {banks.map((b)=><SelectItem key={b.id} value={b.id}>{b.name} · {b.currency} ({fmt(b.balance,b.currency)})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Hacia</Label>
              <Select value={transferForm.to_bank_id} onValueChange={(v)=>setTransferForm({...transferForm,to_bank_id:v})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Banco destino..."/></SelectTrigger>
                <SelectContent>
                  {banks.filter((b)=>b.id!==transferForm.from_bank_id).map((b)=><SelectItem key={b.id} value={b.id}>{b.name} · {b.currency}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Monto</Label>
              <Input type="number" step="0.01" min="0.01" value={transferForm.amount} onChange={(e)=>setTransferForm({...transferForm,amount:e.target.value})} required className="mt-1 numeric"/>
            </div>
            {needsXR && (
              <div>
                <Label className="text-xs uppercase tracking-wider">Tipo de cambio</Label>
                <Input type="number" step="0.01" value={transferForm.exchange_rate} onChange={(e)=>setTransferForm({...transferForm,exchange_rate:e.target.value})} required className="mt-1 numeric" placeholder="1 USD = X MXN"/>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider">Nota</Label>
              <Input value={transferForm.note} onChange={(e)=>setTransferForm({...transferForm,note:e.target.value})} className="mt-1"/>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setShowTransfer(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277] text-white">Transferir</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transactions */}
      <Dialog open={!!txBank} onOpenChange={(o)=>{ if (!o) { setTxBank(null); setTxs([]); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Movimientos · {txBank?.name}</DialogTitle>
            <div className="flex items-center justify-between gap-3 mt-1">
              <div className="text-xs text-zinc-500">Saldo: <span className="numeric font-semibold">{fmt(txBank?.balance, txBank?.currency)}</span></div>
              {txBank && (
                <Button
                  size="sm"
                  onClick={()=>{ setExpenseBank(txBank); setTxBank(null); setTxs([]); }}
                  className="bg-[#7E57C2] hover:bg-[#6943B0] text-white gap-1 h-7 text-xs"
                  data-testid="modal-register-expense-btn"
                >
                  <MinusCircle size={12} weight="bold"/> Registrar gasto
                </Button>
              )}
            </div>
          </DialogHeader>
          <table className="w-full text-sm" data-testid="tx-table">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-left px-3 py-2">Nota</th>
                <th className="text-right px-3 py-2">Monto</th>
                <th className="text-right px-3 py-2">Saldo después</th>
                <th className="text-right px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {txs.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-zinc-400 dotted-bg">Sin movimientos</td></tr>}
              {txs.map((t) => {
                const meta = TX_LABELS[t.type] || { label: t.type, color: "#52525B", Icon: Receipt };
                const Icon = meta.Icon;
                const editable = t.type === "deposit" || t.type === "withdraw" || t.type === "transfer_in" || t.type === "transfer_out";
                const lockedReason = t.sale_id
                  ? "Vinculado a venta · elimínalo desde Notas de Venta"
                  : (t.type === "expense" || t.type === "expense_reversal")
                    ? "Movimiento de gasto · edítalo desde Gastos"
                    : "";
                const deletable = editable;
                const linkedToSale = !!t.sale_id;
                const openSale = () => {
                  if (!linkedToSale) return;
                  // Close the movements modal, then navigate to the sale note.
                  setTxBank(null); setTxs([]);
                  navigate(`/sales?open=${t.sale_id}`);
                };
                return (
                  <tr
                    key={t.id}
                    className={`border-t border-zinc-100 ${linkedToSale ? "cursor-pointer hover:bg-[#002FA7]/5" : ""}`}
                    onClick={linkedToSale ? openSale : undefined}
                    title={linkedToSale ? "Abrir nota de venta" : undefined}
                    data-testid={`tx-row-${t.id}`}
                  >
                    <td className="px-3 py-2 text-zinc-600 text-xs">{new Date(t.created_at).toLocaleString("es-MX")}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-sm" style={{ background: meta.color+"15", color: meta.color }}>
                        <Icon size={11} weight="bold"/> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {linkedToSale ? (
                        <span className="text-[#002FA7] font-semibold hover:underline" data-testid={`tx-note-link-${t.id}`}>
                          {t.note || "—"} <span className="text-[10px] text-zinc-400">↗</span>
                        </span>
                      ) : (
                        <span className="text-zinc-600">{t.note || "—"}</span>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right numeric font-semibold ${t.amount >= 0 ? "text-[#00873D]" : "text-[#E0000F]"}`}>
                      {t.amount >= 0 ? "+" : ""}{fmt(t.amount, t.currency)}
                    </td>
                    <td className="px-3 py-2 text-right numeric">{fmt(t.balance_after, t.currency)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {editable && (
                        <button onClick={() => startEditTx(t)} className="p-1.5 hover:bg-blue-100 text-[#002FA7] rounded" title="Editar" data-testid={`tx-edit-${t.id}`}>
                          <PencilSimple size={12}/>
                        </button>
                      )}
                      {deletable && (
                        <button onClick={() => setDeletingTx(t)} className="p-1.5 hover:bg-red-100 text-[#E0000F] rounded ml-1" title="Eliminar" data-testid={`tx-delete-${t.id}`}>
                          <Trash size={12}/>
                        </button>
                      )}
                      {!editable && !deletable && lockedReason && (
                        <span className="text-[10px] text-zinc-400 italic" title={lockedReason}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar banco?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <b>{toDelete?.name}</b>. Solo permitido si el saldo es cero y sin historial.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#E0000F] hover:bg-[#b8000c] text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit transaction dialog */}
      <Dialog open={!!editingTx} onOpenChange={(o)=>{ if (!o) setEditingTx(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Editar movimiento</DialogTitle>
          </DialogHeader>
          {editingTx && (() => {
            const isTransfer = editingTx.type === "transfer_in" || editingTx.type === "transfer_out";
            return (
            <form onSubmit={submitEditTx} className="space-y-3">
              <div className={`p-3 text-xs border ${isTransfer ? "bg-[#7E57C2]/5 border-[#7E57C2]/30" : "bg-zinc-50 border-zinc-200"}`}>
                <div className="text-zinc-500 font-mono-data uppercase tracking-wider">Tipo</div>
                <div className="font-semibold text-zinc-800 mt-0.5">
                  {TX_LABELS[editingTx.type]?.label || editingTx.type}
                </div>
                <div className="text-zinc-500 font-mono-data uppercase tracking-wider mt-2">Fecha</div>
                <div className="text-zinc-600 mt-0.5">{new Date(editingTx.created_at).toLocaleString("es-MX")}</div>
                {isTransfer && (
                  <div className="mt-2 pt-2 border-t border-[#7E57C2]/20 text-[#7E57C2] flex items-center gap-1.5">
                    <ArrowsLeftRight size={12} weight="bold"/>
                    <span>Los cambios se aplican en <b>ambos bancos</b> (origen y destino).</span>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">
                  Monto {isTransfer && <span className="text-[10px] text-[#7E57C2] normal-case font-normal ml-1">({editingTx.type === "transfer_out" ? "origen" : "destino"})</span>} *
                </Label>
                <Input type="number" step="0.01" min="0" required value={txDraft.amount} onChange={(e) => setTxDraft({ ...txDraft, amount: e.target.value })} className="mt-1 numeric" data-testid="tx-edit-amount"/>
                <p className="text-[10px] text-zinc-400 mt-1">
                  {isTransfer
                    ? "Se ajustan ambos lados de la transferencia. En transferencias entre monedas distintas se preserva el tipo de cambio original."
                    : "El saldo del banco se ajustará por la diferencia."}
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Nota</Label>
                <Input value={txDraft.note} onChange={(e) => setTxDraft({ ...txDraft, note: e.target.value })} className="mt-1" data-testid="tx-edit-note"/>
                {isTransfer && <p className="text-[10px] text-zinc-400 mt-1">La nota se sincroniza en ambos lados.</p>}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditingTx(null)}>Cancelar</Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="tx-edit-submit">Guardar</Button>
              </DialogFooter>
            </form>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete transaction confirmation */}
      <AlertDialog open={!!deletingTx} onOpenChange={(o)=>{ if (!o) setDeletingTx(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar movimiento?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingTx && (
                <>
                  Se eliminará el movimiento <b>{TX_LABELS[deletingTx.type]?.label || deletingTx.type}</b> de <b className="numeric">{fmt(Math.abs(deletingTx.amount), deletingTx.currency)}</b> y el saldo del banco se revertirá.
                  {(deletingTx.type === "transfer_in" || deletingTx.type === "transfer_out") && (
                    <div className="mt-2 text-amber-700 text-xs">⚠ Esta es una transferencia: se eliminará también el lado contraparte del banco relacionado.</div>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteTx} className="bg-[#E0000F] hover:bg-[#b8000c] text-white" data-testid="tx-delete-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ExpenseFormDialog
        open={!!expenseBank}
        onOpenChange={(o)=>{ if (!o) setExpenseBank(null); }}
        onSaved={()=>{ setExpenseBank(null); load(); }}
        lockBank={expenseBank}
      />
    </div>
  );
}
